/**
 * The Terms of Use, in the pieces the app needs: the text itself, the "I have read and accept" box
 * the forms that create an account carry, and the dialog that asks somebody already signed in to
 * accept a version they have not.
 *
 * The link in the box opens the text over the form rather than navigating to /terms: a person
 * halfway through signing up keeps what they typed, and the desktop app's window has no second tab
 * to open it in. /terms is still there for a link from anywhere else.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { clearSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { TERMS_URL, TERMS_VERSION, useTermsDocument, type TermsDocument } from '../lib/termsOfUse'
import { PRIVACY_URL, usePrivacyDocument } from '../lib/privacyPolicy'
import { Modal } from './Modal'

/** A legal text, headed and sectioned: the Terms of Use and the Privacy Policy are the same shape. */
function LegalArticle({ document, failed, fallbackTitle, url, headingLevel }: { document: TermsDocument | undefined; failed: boolean; fallbackTitle: string; url: string; headingLevel: 1 | 2 }) {
  const { t } = useI18n()
  const Title = headingLevel === 1 ? 'h1' : 'h2'
  const Section = headingLevel === 1 ? 'h2' : 'h3'
  if (!document) {
    return (
      <article className="terms-text" aria-busy={!failed}>
        <Title className="terms-text__title">{fallbackTitle}</Title>
        {failed ? (
          <p className="small" role="alert">
            {t('error.generic')} <a href={url} target="_blank" rel="noreferrer">{url}</a>
          </p>
        ) : (
          <p className="small muted">…</p>
        )}
      </article>
    )
  }
  return (
    <article className="terms-text">
      <Title className="terms-text__title">{document.title}</Title>
      <p className="tiny muted terms-text__version">{document.versionLabel}</p>
      {document.intro.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
      {document.sections.map((section) => (
        // A section may carry an anchor ("excluir-conta"), so a link from outside lands on it.
        <section key={section.heading} id={section.id}>
          <Section className="terms-text__heading">{section.heading}</Section>
          {section.paragraphs?.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          {section.items && (
            <ul>
              {section.items.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
          )}
        </section>
      ))}
    </article>
  )
}

/** The whole Terms of Use, in the reader's language; fetched the first time it is shown. */
export function TermsText({ headingLevel = 1 }: { headingLevel?: 1 | 2 }) {
  const { t, locale } = useI18n()
  const { document, failed } = useTermsDocument(locale)
  return <LegalArticle document={document} failed={failed} fallbackTitle={t('terms.title')} url={TERMS_URL} headingLevel={headingLevel} />
}

/** The whole Privacy Policy (docs/refactor/65), the same way. */
export function PrivacyText({ headingLevel = 1 }: { headingLevel?: 1 | 2 }) {
  const { t, locale } = useI18n()
  const { document, failed } = usePrivacyDocument(locale)
  return <LegalArticle document={document} failed={failed} fallbackTitle={t('privacy.title')} url={PRIVACY_URL} headingLevel={headingLevel} />
}

/** "I have read and accept the Terms of Use", with the terms one click away. */
export function TermsCheckbox({ checked, onChange, disabled = false }: { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  const { t } = useI18n()
  const [reading, setReading] = useState<'terms' | 'privacy' | null>(null)

  return (
    <>
      <label className="checkbox terms-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
          data-testid="terms-checkbox"
        />
        <span className="small">
          {t('terms.acceptPrefix')}{' '}
          <a
            href="/terms"
            onClick={(event) => {
              // A plain click reads them here; a middle click or "open in new tab" still gets the page.
              event.preventDefault()
              setReading('terms')
            }}
          >
            {t('terms.link')}
          </a>{' '}
          {t('terms.and')}{' '}
          <a
            href="/privacy"
            onClick={(event) => {
              event.preventDefault()
              setReading('privacy')
            }}
          >
            {t('privacy.title')}
          </a>
        </span>
      </label>
      {/* Outside the label, or every click inside the dialog would tick the box. */}
      {reading && (
        <Modal title={reading === 'terms' ? t('terms.title') : t('privacy.title')} onClose={() => setReading(null)} wide>
          <div className="terms-dialog__text">
            {reading === 'terms' ? <TermsText headingLevel={2} /> : <PrivacyText headingLevel={2} />}
          </div>
        </Modal>
      )}
    </>
  )
}

/**
 * Over the whole app, for somebody signed in whose account has not accepted the terms in force —
 * an account from before them, or from an older version. Nothing behind it can be used until they
 * accept or sign out; there is deliberately no close button.
 */
export function TermsGate() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [checked, setChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const accept = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await api.me.acceptTerms()
      // "me" says termsAccepted now; reading it again is what takes this dialog away.
      await queryClient.invalidateQueries({ queryKey: ['me'], exact: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal__backdrop terms-gate">
      <div className="panel modal modal--wide terms-gate__box" role="dialog" aria-modal="true" aria-labelledby="terms-gate-title">
        <header className="panel__header">
          <h2 className="panel__title" id="terms-gate-title">{t('terms.gate.title')}</h2>
        </header>
        <div className="panel__body stack">
          <p className="small" style={{ margin: 0 }}>{t('terms.gate.body', { version: TERMS_VERSION })}</p>
          <div className="terms-gate__text" tabIndex={0} aria-label={t('terms.title')}>
            <TermsText headingLevel={2} />
          </div>
          <TermsCheckbox checked={checked} onChange={setChecked} disabled={busy} />
          {error && <div className="field__error" role="alert">{error}</div>}
          <div className="row row--between terms-gate__actions">
            <button type="button" className="btn btn--ghost" onClick={clearSession} disabled={busy}>
              {t('nav.signOut')}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => void accept()} disabled={!checked || busy}>
              {busy ? t('terms.gate.accepting') : t('terms.gate.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
